import axios, { AxiosResponse } from "axios";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { pb_excl, speechRecogPhraseListLetters, convoSyllabicStressWords, CONVO_SYLLABIC_STRESS_WORDS, VOICE_ELEVEN_CODE_TO_ID_MAP } from "../lsts";
import { escapeSSML } from "../util_glob";
import { PrefixSuffixSplitResult } from "./../interfaces";

const BACKEND_URI = "";

// ── TTS provider abstraction ──────────────────────────────────────────
export interface TTSSynthResult {
    data: ArrayBuffer;
    mimeType: string;
}
export interface TTSSynthesizer {
    synthesize(text: string, voiceVersion: string, lang: string, prosodyRate: number): Promise<TTSSynthResult>;
    close(): void;
}

class AzureTTSSynthesizer implements TTSSynthesizer {
    private synth: sdk.SpeechSynthesizer | null;
    constructor(voiceVersion: string, voiceLang: string) {
        this.synth = getSynthOnly(voiceVersion, voiceLang);
    }
    synthesize(text: string, voiceVersion: string, lang: string, prosodyRate: number): Promise<TTSSynthResult> {
        return new Promise((resolve, reject) => {
            if (!this.synth) return reject("azure_synth_not_initialized");
            this.synth.speakSsmlAsync(
                prepSsml(text, voiceVersion, lang, prosodyRate),
                result => {
                    if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted && result.audioData) {
                        resolve({ data: result.audioData, mimeType: "audio/wav" });
                    } else {
                        reject(result.errorDetails || "azure_synth_error");
                    }
                },
                error => reject(error)
            );
        });
    }
    close(): void {
        this.synth?.close();
        this.synth = null;
    }
}

class ElevenLabsTTSSynthesizer implements TTSSynthesizer {
    private voiceId: string;
    constructor(voiceId: string) {
        this.voiceId = voiceId;
    }
    async synthesize(text: string, _voiceVersion: string, _lang: string, prosodyRate: number): Promise<TTSSynthResult> {

        const apiKey = import.meta.env.VITE_ELEVEN_K;
        const speed = Number((1 + 0.005 * prosodyRate).toFixed(1));
        console.log("eleven_tts_synth", text, prosodyRate, speed);
        const resp = await fetch(
            `https://api.elevenlabs.io/v1/text-to-speech/${this.voiceId}`,
            {
                method: "POST",
                headers: { "xi-api-key": apiKey, "Content-Type": "application/json", Accept: "audio/mpeg" },
                body: JSON.stringify({
                    text,
                    model_id: "eleven_flash_v2_5",
                    output_format: "mp3_44100_128",
                    voice_settings: { stability: 0.3, similarity_boost: 0.1, style: 1, speed },
                    optimize_streaming_latency: 4
                })
            }
        );
        if (!resp.ok) throw new Error(`eleven_tts_error_${resp.status}`);
        const data = await resp.arrayBuffer();
        return { data, mimeType: "audio/mpeg" };
    }
    close(): void { /* stateless */ }
}

export const createSynthesizer = (ttsProvider: number, voiceVersion: string, voiceLang: string): TTSSynthesizer | null => {
    if (ttsProvider >= 2) {
        const voiceId = VOICE_ELEVEN_CODE_TO_ID_MAP[ttsProvider];
        if (!voiceId) { console.error("unknown_eleven_voice_code", ttsProvider); return null; }
        return new ElevenLabsTTSSynthesizer(voiceId);
    }
    return new AzureTTSSynthesizer(voiceVersion, voiceLang);
};
// ── end TTS provider abstraction ──────────────────────────────────────

export const fetchSpeechAOI = async (text: string): Promise<HTMLAudioElement | null> => {
    const s = import.meta.env.VITE_TTS_OAI_S;
    const a = import.meta.env.VITE_TTS_OAI_A;
    const d = import.meta.env.VITE_TTS_OAI_D;
    const k = import.meta.env.VITE_TTS_OAI_K;

    const ttsUrl = `https://${s}.openai.azure.com/openai/deployments/tts/audio/speech?api-version=${a}`;

    const requestBody = {
        input: text,
        model: `${d}`,
        voice: "alloy",
        speed: 1.2,
        voiceP: "en-US-AvaNeural"
    };

    try {
        const speechResponse: AxiosResponse<ArrayBuffer> = await axios.post(ttsUrl, requestBody, {
            headers: {
                "api-key": `${k}`,
                "Content-Type": "application/json",
                Accept: "audio/mp3"
            },
            responseType: "arraybuffer"
        });

        const audioBlob = new Blob([speechResponse.data], { type: "audio/mp3" });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        return audio;
    } catch (error) {
        console.error("Error fetching speech:", error);
        return null;
    }
};
export const fetchSpeechSDK = async (text: string) => {
    const k = import.meta.env.VITE_TTS_OAI_K;
    const r = import.meta.env.VITE_TTS_OAI_R;
    const speechConfig = sdk.SpeechConfig.fromSubscription(`${k}`, `${r}`);

    speechConfig.speechSynthesisVoiceName = "en-US-NovaMultilingualNeural";
    const synthesizer = new sdk.SpeechSynthesizer(speechConfig);
    synthesizer.speakTextAsync(
        text,
        result => {
            if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
                import.meta.env.DEV === true && console.log("speech_synth_ok");
            } else {
                import.meta.env.DEV === true && console.log("speech_synth_errr", result.errorDetails);
            }
            synthesizer.close();
        },
        error => {
            console.error("Error: " + error);
            synthesizer.close();
        }
    );
};
export const getClearTextForAudio = (page: number) => {
    const items = document.querySelectorAll(`.pdf_itm_${page - 1}`);
    return Array.from(items)
        .filter(
            item =>
                item.textContent != null && // Exclude if string is empty
                !/^\d+$/.test(item.textContent) && // Exclude if string is only digits
                !pb_excl.includes(item.textContent.toLowerCase().trim()) && // Exclude if string is in the exclusion array
                item.textContent.trim() !== "|"
        )
        .map(item => item.textContent)
        .join(" ")
        .replace("&", " and ");
};
export const determineBGUS = (text: string) => {
    const containsCyrillic = /[а-яА-ЯЁё]/.test(text);
    let voiceP = "en-US-AvaNeural";
    if (containsCyrillic) {
        voiceP = "bg-BG-KalinaNeural";
    }
    return voiceP;
};
export const ttsTk = async (tkUrl: string, apk: any) => {
    return await axios.post(
        tkUrl,
        {},
        {
            headers: {
                "Ocp-Apim-Subscription-Key": `${apk}`,
                "Content-Type": "application/x-www-form-urlencoded"
            }
        }
    );
};
export const getTTSAudio = async (ttsUrl: string, text: string, token: any) => {
    return await axios.post(
        ttsUrl,
        `<speak version='1.0' xml:lang='bg-BG'><voice xml:lang='bg-BG' xml:gender='Female' name='bg-BG-KalinaNeural'>${text}</voice></speak>`,
        {
            headers: {
                Authorization: `${import.meta.env.VITE_BR} ${token}`,
                "Content-Type": "application/ssml+xml",
                "X-Microsoft-OutputFormat": "audio-16khz-128kbitrate-mono-mp3"
            },
            responseType: "blob"
        }
    );
};
export const fetchBase64 = async (url: string) => {
    try {
        const response = await axios.get(url, { responseType: "blob" });
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => (reader.result ? resolve(reader.result.toString().split(",")[1]) : "");
            reader.onerror = reject;
            reader.readAsDataURL(response.data);
        });
    } catch (error) {
        console.error("errr_fetch_tts", error);
        return null;
    }
};
export const writeString = (view: DataView, offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
};
export const createWavBuffer = (samples: Int16Array, sampleRate: number): ArrayBuffer => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const bytesPerSample = 2; // 16-bit Audio
    const numChannels = 1; // Mono
    const byteRate = sampleRate * numChannels * bytesPerSample;
    const blockAlign = numChannels * bytesPerSample;
    const bitsPerSample = 16;
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + samples.length * bytesPerSample, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(view, 36, "data");
    view.setUint32(40, samples.length * bytesPerSample, true);
    for (let i = 0; i < samples.length; i++) {
        view.setInt16(44 + i * 2, samples[i], true);
    }
    return buffer;
};
export const playWavBuffer = (buffer: ArrayBuffer) => {
    const audioContext = new AudioContext();
    audioContext.decodeAudioData(
        buffer,
        decodedData => {
            const source = audioContext.createBufferSource();
            source.buffer = decodedData;
            source.connect(audioContext.destination);
            source.start(0);
        },
        error => {
            console.error("Error decoding audio data", error);
        }
    );
};
export const getSynthOnly = (voiceVersion: string, voiceLang: string): sdk.SpeechSynthesizer | null => {
    const reg = import.meta.env.VITE_STT_R;
    const apk = import.meta.env.VITE_STT_K;
    try {
        const speechConfig = sdk.SpeechConfig.fromSubscription(apk, reg);
        speechConfig.speechSynthesisVoiceName = voiceVersion;
        speechConfig.speechSynthesisLanguage = voiceLang;

        const audioOutputStream = sdk.AudioOutputStream.createPullStream();
        const audioConfig = sdk.AudioConfig.fromStreamOutput(audioOutputStream);
        const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);
        return synthesizer;
    } catch (error: any) {
        console.error("errr_creating_synth", error);
        return null;
    }
};
export const getVoiceVersion = (text: string | undefined, selected_lang: string) => {
    if (selected_lang && selected_lang !== "auto") {
        if (selected_lang == "bg-BG") {
            return { voice: import.meta.env.VITE_VID_BG, language: "bg-BG" };
        } else {
            return { voice: import.meta.env.VITE_VID_ML, language: selected_lang };
        }
    }
    return /[а-яА-ЯЁё]/.test(text || "")
        ? { voice: import.meta.env.VITE_VID_BG, language: "bg-BG" }
        : { voice: import.meta.env.VITE_VID_EN, language: "en-US" };
};
export const createVisGradient = (canvasCtx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, height: number) => {
    const gradient = canvasCtx.createLinearGradient(0, canvas.height - height, 0, canvas.height);
    gradient.addColorStop(0, "rgba(255, 69, 0, 1)");
    gradient.addColorStop(1, "rgba(255, 140, 0, 1)");
    return gradient;
};
export const prepRecog = (convoLangGroupSetting: string, assistantType: string) => {
    const apk = import.meta.env.VITE_STT_K;
    const reg = import.meta.env.VITE_STT_R;
    const lngs = convoLangGroupSetting == "auto" ? ["bg-BG", "en-US"] : convoLangGroupSetting == "bg-BG" ? ["bg-BG"] : ["en-US"];

    let segm_timeout = assistantType == "cca" ? "800" : "2000";
    let end_timeout = assistantType == "cca" ? "1000" : "1000";
    let init_timeout = "10000";

    const speechConfig = sdk.SpeechConfig.fromSubscription(apk, reg);
    const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();

    speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_LanguageIdMode, "Continuous");
    speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, init_timeout);
    speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, segm_timeout);
    speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, end_timeout);
    speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationStrategy, "Semantic");
    speechConfig.setProperty(sdk.PropertyId.SpeechServiceResponse_PostProcessingOption, "TrueText");
    const newRecognizer = sdk.SpeechRecognizer.FromConfig(speechConfig, sdk.AutoDetectSourceLanguageConfig.fromLanguages(lngs), audioConfig);
    if (speechRecogPhraseListLetters.length > 0) {
        const phraseList = sdk.PhraseListGrammar.fromRecognizer(newRecognizer);
        phraseList.addPhrases(speechRecogPhraseListLetters);
    }
    return newRecognizer;
};
export const applyAmendedPhraseList = (recognizer: sdk.SpeechRecognizer, amendValues: string[]) => {
    const phraseListGrammar = sdk.PhraseListGrammar.fromRecognizer(recognizer);

    let phraseListAmended = [
        ...amendValues.map(phrase => {
            try {
                return phrase.toString().split("").join(" ");
            } catch (error) {
                return phrase;
            }
        }),
        ...speechRecogPhraseListLetters
    ];
    import.meta.env.DEV && console.log("▢expected▢", phraseListAmended);
    if (phraseListAmended.length > 450) {
        phraseListAmended = phraseListAmended.slice(0, 450); // Limit to 100 phrases
    }
    if (phraseListAmended.length == 0) {
        phraseListAmended = speechRecogPhraseListLetters; // Limit to 100 phrases
    }

    phraseListGrammar.clear();
    import.meta.env.DEV && console.log("‹phraseListAmended›", phraseListAmended);
    phraseListAmended.forEach(phrase => {
        phraseListGrammar.addPhrase(phrase);
    });
};
export const prepSsml = (txt: string, voiceVersion: string, lang: string, prosodyRatePerc = 40) => {
    let preptxt = addSyllabicStress(fixSingleLettersAndDigits(escapeSSML(txt), lang), lang);
    let ssml = `<speak version="1.0"
       xmlns="http://www.w3.org/2001/10/synthesis"
       xmlns:mstts="http://www.w3.org/2001/mstts"
       xmlns:emo="http://www.w3.org/2009/10/emotionml"
       xml:lang="${lang}">
	<voice name="${voiceVersion}">
		<lang xml:lang="${lang}">
			<s/>
			<prosody rate="${prosodyRatePerc}%">
            <mstts:express-as style="empathetic">${preptxt}</mstts:express-as>
			</prosody>
			<s/>
		</lang>
	</voice>
</speak>
`;
    import.meta.env.DEV === true && console.log("¦SSML¦", ssml);
    return ssml;
};
export const prepSsmlDocReader = (txt: string, voiceVersion: string, lang: string, prosodyRatePerc = 40) => {
    let preptxt = addSyllabicStress(fixSingleLettersAndDigits(escapeSSML(txt), lang), lang);
    let ssml = `<speak version="1.0"
       xmlns="http://www.w3.org/2001/10/synthesis"
       xmlns:mstts="http://www.w3.org/2001/mstts"
       xmlns:emo="http://www.w3.org/2009/10/emotionml"
       xml:lang="${lang}">
	<voice name="${voiceVersion}">
		<lang xml:lang="${lang}">
			<s/>
			<prosody rate="${prosodyRatePerc}%">
            <mstts:express-as style="empathetic">${preptxt}</mstts:express-as>
			</prosody>
			<s/>
		</lang>
	</voice>
</speak>
`;
    import.meta.env.DEV === true && console.log("¦SSML¦", preptxt);
    return ssml;
};
// INTERNAL
const escapeConvoSSMLRegExp = (text: string) => {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // ESCAPE SPECIAL CHARACTERS
};
const addSyllabicStress = (text: string, lang: string) => {
    if (lang !== "bg-BG") return text; // ONLY APPLY SYLLABIC STRESS FOR BULGARIAN LANGUAGE
    Object.keys(CONVO_SYLLABIC_STRESS_WORDS).forEach(wordWithBrackets => {
        const wordWithoutBrackets = wordWithBrackets.replace(/\[|\]/g, ""); // REMOVE BRACKETS TO MATCH NORMAL TEXT
        const escapedWord = escapeConvoSSMLRegExp(wordWithoutBrackets);
        const regex = new RegExp(`(?:^|\\s|[,.!?:])(${escapedWord})(?=$|\\s|[,.!?:;])`, "gi");
        text = text.replace(regex, match => `<phoneme alphabet="ipa" ph="${CONVO_SYLLABIC_STRESS_WORDS[wordWithBrackets]}">${match}</phoneme>`);
    });
    return text;
};
const fixSingleLettersAndDigits = (txt: string, lang: string) => {
    import.meta.env.DEV && console.log("lang¦", lang);
    let excludeWords = new Set(["I", "i"]);
    if (lang == "bg-BG") {
        excludeWords = new Set(["a", "i", "а", "о", "е", "и", "в", "у", "с", "я"]);
    }

    /*
    This matches:
        A single character (letter or digit) that is:
        Surrounded by non-word characters (or start/end of line)
        For example: " a " → matches "a" because it's isolated
    */

    return txt.replace(/(^|[^А-Яа-яA-Za-z0-9])([А-Яа-яA-Za-z0-9])(?=[^А-Яа-яA-Za-z0-9]|$)/g, (fullMatch, boundaryBefore, singleChar, offset, fullStr) => {
        const isLowercase = singleChar == singleChar.toLowerCase();
        if (excludeWords.has(singleChar.toLowerCase())) return boundaryBefore + singleChar;

        function getPrevWord(str: string, startPos: number) {
            let i = startPos;
            while (i >= 0 && str[i] === " ") i--;
            if (i < 0) return "";
            let end = i;
            while (i >= 0 && /[А-Яа-яA-Za-z0-9]/.test(str[i])) i--;
            return str.slice(i + 1, end + 1);
        }
        function getNextWord(str: string, startPos: number) {
            let i = startPos;
            while (i < str.length && str[i] === " ") i++;
            if (i >= str.length) return "";
            let start = i;
            while (i < str.length && /[А-Яа-яA-Za-z0-9]/.test(str[i])) i++;

            return str.slice(start, i);
        }

        const prevWord = getPrevWord(fullStr, offset - 1);
        const nextWord = getNextWord(fullStr, offset + 1);
        const prevIsSingleWord = prevWord.length === 1;
        const nextIsSingleWord = nextWord.length === 1;

        if (!prevIsSingleWord && !nextIsSingleWord) return boundaryBefore + singleChar;
        return boundaryBefore + `<emphasis level="moderate"><break time="40ms"/><prosody rate="medium">${singleChar}</prosody><break time="40ms"/></emphasis>`;
    });
};
// LEGACY
export const getSynth = (audioConfig: sdk.AudioConfig, voiceVersion: string, voiceLang: string): sdk.SpeechSynthesizer | null => {
    const reg = import.meta.env.VITE_STT_R;
    const apk = import.meta.env.VITE_STT_K;
    try {
        const speechConfig = sdk.SpeechConfig.fromSubscription(apk, reg);
        speechConfig.speechSynthesisVoiceName = voiceVersion;
        speechConfig.speechSynthesisLanguage = voiceLang;
        const synthesizer = new sdk.SpeechSynthesizer(speechConfig);
        //synthesizer.wordBoundary = (s, e) => {
        //    import.meta.env.DEV === true && console.log("word_boundary", e.audioOffset);
        //};
        return synthesizer;
    } catch (error: any) {
        console.error("errr_creating_synth", error);
        return null;
    }
};
export const callWhspr = async (azureOpenAIEndpoint: string | undefined, formData: FormData, azureOpenAIKey: string | undefined) => {
    return await axios.post(`${azureOpenAIEndpoint}/openai/deployments/whisper/audio/transcriptions?api-version=2023-09-01-preview`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
            "api-key": azureOpenAIKey
        },
        params: {
            response_format: "json"
        }
    });
};

// Helper function to append to a file — placeholder, implement based on your environment
function appendSet(phrases: string[], fileName: string) {
    // You must implement this yourself
    import.meta.env.DEV && console.log(`Writing ${phrases.length} phrases to ${fileName}`);
}

const prefixSuffixUniqueSplit = (strings: string[], tokenCap: number = 500, minLen: number = 3): PrefixSuffixSplitResult => {
    let bestTotal = Infinity;
    let best: PrefixSuffixSplitResult = [null, new Set(), new Set()];
    const maxLen = Math.max(...strings.map(s => s.length));
    for (let n = minLen; n <= maxLen - minLen; n++) {
        const prefixSet = new Set<string>();
        const suffixSet = new Set<string>();
        for (const s of strings) {
            prefixSet.add(s.slice(0, n));
            suffixSet.add(s.slice(n));
        }
        const total = prefixSet.size + suffixSet.size;
        if (total < bestTotal) {
            bestTotal = total;
            best = [n, prefixSet, suffixSet];
        }
        if (total <= tokenCap) return [n, prefixSet, suffixSet];
    }
    return best;
};
